<script>
import appConfig from '@src/app.config'
import Layout from '@layouts/main.vue'
import Header from '@components/header.vue'
import { store } from '../../state/store'
import SystemTestService from '@services/business/system-test.service'
import TEST_VARIANCE_MINUTES from '../../router/layouts/main.vue'
import router from '../index'
import FooterComponent from '@components/footer.vue'
import ErrorAlert from '@components/error-alert.vue'
import { phoneNumbers } from '../../models/enums'

export default {
  page: {
    title: 'System Test',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: { Layout, Header, FooterComponent, ErrorAlert },
  data() {
    return {
      state: store.state,
      testDuration: 2,
      testDurationOptionsBase: [
        { text: '2 HOUR', value: 2 },
        { text: '4 HOUR', value: 4 },
      ],
      isLoading: false,
      generalPhoneNumber: phoneNumbers.GENERAL,
    }
  },
  computed: {
    onTestLimitReached: function() {
      var onTestTime = this.getSystemOnTestTime()
      return (
        (onTestTime.hour === 10 && onTestTime.minute > TEST_VARIANCE_MINUTES) ||
        onTestTime.hour > 10
      )
    },
    onTestTimerLessthan4Hours: function() {
      var onTestTime = this.getSystemOnTestTime()
      return (
        (onTestTime.hour === 8 && onTestTime.minute > TEST_VARIANCE_MINUTES) ||
        onTestTime.hour > 8
      )
    },
    testDurationOptions: function() {
      if (this.onTestTimerLessthan4Hours) {
        return this.testDurationOptionsBase.slice(0, 1)
      } else {
        return this.testDurationOptionsBase
      }
    },
  },
  watch: {
    onTestTimerLessthan4Hours(newValue) {
      if (newValue) {
        this.testDuration = 2
      }
    },
  },
  methods: {
    beginSystemTest() {
      this.isLoading = true
      SystemTestService.setSystemOnTest(this.testDuration)
        .then(() => {
          this.isLoading = false
        })
        .catch(() => {
          this.isLoading = false
          store.presentErrorAlert('Unable to put systems on test.')
        })
    },
    extendSystemTest() {
      this.isLoading = true
      SystemTestService.extendSystemTest(this.testDuration)
        .then(() => {
          this.isLoading = false
          store.resetFifteenMinutesAcknowledged()
        })
        .catch(() => {
          this.isLoading = false
          store.presentErrorAlert('Unable to extend system tests.')
        })
    },
    convertMS(ms) {
      var d, h, m, s
      s = Math.floor(ms / 1000)
      m = Math.floor(s / 60)
      s = s % 60
      h = Math.floor(m / 60)
      m = m % 60
      d = Math.floor(h / 24)
      h = h % 24
      h += d * 24
      return { hour: h, minute: m }
    },
    getSystemOnTestTime() {
      return this.convertMS(
        new Date(this.state.systemTest.localExpireDate).getTime() -
          new Date(this.state.systemTest.localEffectiveDate).getTime()
      )
    },
    goToSignalTestPage() {
      router.push('signal-test')
    },
  },
}
</script>

<template>
  <Layout>
    <Header />
    <div :class="$style.system_test_parent_container">
      <div v-show="isLoading" :class="$style.spinner_container">
        <div class="spinner"></div>
      </div>
      <div v-show="!isLoading" :class="$style.system_test_container">
        <div class="text-center">
          <h1 :class="$style.system_test" class="mb-1">System Test</h1>
          <ErrorAlert></ErrorAlert>
        </div>
        <template v-if="onTestLimitReached">
          <div class="text-center">
            <h4 class="text-primary">You have reached the on-test limit.</h4>
          </div>
          <div class="text-center">
            <h3 class="text-primary">Call Field Services for help.</h3>
          </div>
          <div class="text-center">
            <span
              ><a
                id="link-on-test-limit-phone"
                class="text-primary"
                :href="'tel:' + generalPhoneNumber"
              >
                {{ generalPhoneNumber }}
              </a></span
            >
          </div>
        </template>
        <template v-else>
          <div class="text-center">
            <h3 :class="$style.duration_header">Test Duration</h3>
          </div>
          <div class="form-group text-center px-5 mt-2">
            <div class="row">
              <div
                v-for="option in testDurationOptions"
                :key="option.value"
                :value="option.value"
                class="col"
              >
                <h5 :class="$style.duration_time_header">{{ option.text }}</h5>
              </div>
            </div>
            <div class="row">
              <div
                v-for="option in testDurationOptions"
                :key="option.value"
                :value="option.value"
                class="col"
              >
                <input
                  v-model="testDuration"
                  :class="$style.enhance_radio_btn"
                  type="radio"
                  :value="option.value"
                />
              </div>
            </div>
          </div>

          <div class="text-center mt-4">
            <button
              v-if="!state.systemTest.onTest"
              id="btn-site-on-test"
              class="btn btn-primary"
              @click="beginSystemTest"
            >
              Begin Test
            </button>
            <button
              v-if="state.systemTest.onTest"
              id="btn-extend-site-test"
              class="btn btn-primary"
              @click="extendSystemTest"
            >
              Extend Test
            </button>
          </div>
        </template>
      </div>
      <FooterComponent
        primary-btn-text="SIGNAL TEST"
        :primary-btn-callback="goToSignalTestPage"
        :primary-btn-disable-state="!state.systemTest.onTest"
        primary-btn-id="btn-signal-test"
      ></FooterComponent>
    </div>
  </Layout>
</template>

<style lang="scss" module>
@import '@design';
.system_test_container {
  max-width: $size-parent-container-width-max;
  padding: 1em;
  margin: auto;
}

.system_test_parent_container {
  position: relative;
  top: $height-status-bar;
  align-items: center;
  width: 100%;
  text-align: center;
}

.spinner_container {
  position: fixed;
  top: 0;
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
}

.spinner {
  display: flex;
  margin: 0 auto;
}

.duration_time_header {
  font-size: 1em;
  font-weight: 900;
  color: $color-text-primary;
}

.duration_header {
  margin-top: 0;
  font-weight: 900;
  color: $color-text-primary;
}

.system_test {
  margin-top: 0;
  font-weight: 900;
  color: $color-text-primary;
}

.enhance_radio_btn {
  transform: scale(2.5);
}
</style>
